import React from 'react';
import styles from './skeleton.module.scss';

export interface SkeletonProps {
  /**
   * Variante del skeleton
   * - rect: Rectángulo (default)
   * - text: Línea de texto
   * - circle: Círculo
   */
  variant?: 'rect' | 'text' | 'circle';

  /**
   * Ancho del skeleton. Acepta valores CSS (100%, 200px, etc.)
   */
  width?: string | number;

  /**
   * Alto del skeleton. Acepta valores CSS (100%, 200px, etc.)
   */
  height?: string | number;

  /**
   * Tipo de animación
   * - wave: Efecto shimmer con ola verde Guvery (default)
   * - pulse: Pulso de opacidad
   * - false: Sin animación
   */
  animation?: 'wave' | 'pulse' | false;

  /**
   * Clases CSS adicionales
   */
  className?: string;

  /**
   * Estilos inline adicionales
   */
  style?: React.CSSProperties;
}

/**
 * Skeleton primitivo con animación shimmer personalizada y colores Guvery.
 * Reemplaza Material-UI Skeleton con mayor control sobre estilos.
 *
 * @example
 * // Skeleton de card
 * <Skeleton variant="rect" height={263} />
 *
 * @example
 * // Skeleton de texto sin animación
 * <Skeleton variant="text" animation={false} />
 *
 * @example
 * // Skeleton circular para avatar
 * <Skeleton variant="circle" width={48} height={48} />
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'rect',
  width,
  height,
  animation = 'wave',
  className = '',
  style = {},
}) => {
  const variantClass = styles[variant] || styles.rect;
  const animationClass = animation ? styles[animation] : '';

  const inlineStyles: React.CSSProperties = {
    ...style,
    ...(width !== undefined && { width: typeof width === 'number' ? `${width}px` : width }),
    ...(height !== undefined && {
      height: typeof height === 'number' ? `${height}px` : height,
    }),
  };

  return (
    <div
      className={`${styles.skeleton} ${variantClass} ${animationClass} ${className}`}
      style={inlineStyles}
    />
  );
};

export default Skeleton;
